/**
 * Role draft rules (ORCH-333, ORCH-21 §3.7, §3.12, §11)
 *
 * Purpose: The role editor's state and the rules it applies as you edit, the
 *          same ones RoleService applies on save: a sub-module brings its
 *          parent, a module brings its dependencies, a level brings the
 *          permissions it includes, a scoped permission needs a cell on every
 *          dimension, and GLOBAL-only levels are unavailable in a tenant. The
 *          server re-derives and re-checks everything; these keep the editor
 *          honest about what a save will do.
 */

import { ModuleKey, PermissionKey } from '../../../constants/permissionKeys';
import {
  Dimension,
  ModuleDef,
  ModuleLevel,
  PermissionCatalog,
  PermissionDef,
  Role,
  RoleWrite,
  Scope,
} from '../../../types/authz';

export type Level = ModuleLevel | null;
export type Cells = Record<string, Scope>;

export interface RoleDraft {
  name: string;
  description: string;
  modules: Record<string, ModuleLevel>;
  /** Granted permissions and their cells (empty for an unscoped permission). */
  permissions: Record<string, Cells>;
}

const RANK: Record<string, number> = { read: 1, edit: 2 };
const rank = (level: Level) => (level ? RANK[level] : 0);

export const emptyDraft = (): RoleDraft => ({
  name: '',
  description: '',
  modules: {},
  permissions: {},
});

export const draftFromRole = (role: Role): RoleDraft => ({
  name: role.name,
  description: role.description ?? '',
  modules: { ...role.modules },
  permissions: Object.fromEntries(role.permissions.map((p) => [p.key, { ...p.scopes }])),
});

export const toWrite = (draft: RoleDraft): RoleWrite => ({
  name: draft.name.trim(),
  description: draft.description.trim() || null,
  modules: draft.modules,
  permissions: Object.entries(draft.permissions).map(([key, scopes]) => ({ key, scopes })),
});

const moduleMap = (catalog: PermissionCatalog) =>
  Object.fromEntries(catalog.modules.map((m) => [m.key, m]));
const permissionMap = (catalog: PermissionCatalog) =>
  Object.fromEntries(catalog.permissions.map((p) => [p.key, p]));

/**
 * Does this module's Editable level grant GLOBAL-only permissions (metadata, workflow, AI)?
 * The catalog's includes are cumulative, so Read-only's keys are set aside, as the
 * server's check looks only at what Editable adds.
 */
const editIsGlobalOnly = (module: ModuleDef, permissions: Record<string, PermissionDef>) => {
  const read = new Set(module.includes.read ?? []);
  return (module.includes.edit ?? [])
    .filter((key) => !read.has(key))
    .some((key) => permissions[key]?.global_only);
};

/** The levels this module can be given here; empty when it's GLOBAL-only outside GLOBAL. */
export const levelOptions = (
  module: ModuleDef,
  catalog: PermissionCatalog,
  inGlobal: boolean
): ModuleLevel[] => {
  // The role's tenant decides: a GLOBAL role shows every level it can hold
  if (module.global_only && !inGlobal) return [];
  const permissions = permissionMap(catalog);
  return module.levels.filter(
    (level) => inGlobal || level !== 'edit' || !editIsGlobalOnly(module, permissions)
  );
};

/** The two levels every Area shows (ORCH-415): Hidden is the absence of either. */
export const DISPLAY_LEVELS: ModuleLevel[] = ['read', 'edit'];

/**
 * Which of the two displayed levels this module can be given here (ORCH-415)
 *
 * Editable is shown but unavailable when the module has no Editable level
 * (Analytics, "Not available yet") or its Editable grants GLOBAL-only keys
 * outside GLOBAL. A GLOBAL-only module outside GLOBAL has neither.
 */
export const levelAvailability = (
  module: ModuleDef,
  catalog: PermissionCatalog,
  inGlobal: boolean
): Record<ModuleLevel, boolean> => {
  const options = new Set(levelOptions(module, catalog, inGlobal));
  return { read: options.has('read'), edit: options.has('edit') };
};

/** A module no one chooses: held when any module under it is (the Admin Center). */
export const isDerived = (module: ModuleDef | undefined) => Boolean(module?.derived);

/** The level a derived module shows: Read-only when any module under it is held. */
export const derivedLevel = (draft: RoleDraft, catalog: PermissionCatalog, key: string): Level =>
  catalog.modules.some((m) => m.parent === key && draft.modules[m.key]) ? 'read' : null;

/**
 * The modules the active tenant can't be given (§3.12 rule 2, §11)
 *
 * Purpose: An area is available only where the tenant has it and a role grants
 *          it, so an area the tenant isn't licensed for can't be granted here.
 * Logic: A module whose `feature_toggle` isn't among the tenant's enabled
 *        features is out, and so is anything that would drag one in: a held
 *        child brings its parent at Read-only and a dependant brings its
 *        dependency, so a module whose parent or dependency is out is out too.
 *        Spread to a fixed point, as those chains nest.
 * Input: catalog - the permission catalog; features - the tenant's enabled features
 * Output: the keys that can't be chosen, empty while no module declares a toggle
 */
export const featureDisabled = (catalog: PermissionCatalog, features: string[]): Set<string> => {
  const enabled = new Set(features);
  const out = new Set(
    catalog.modules
      .filter((m) => m.feature_toggle && !enabled.has(m.feature_toggle))
      .map((m) => m.key)
  );
  // Empty while no module declares a toggle, as none does today
  let spreading = out.size > 0;
  while (spreading) {
    spreading = false;
    catalog.modules.forEach((m) => {
      if (out.has(m.key)) return;
      const needs = [...(m.parent ? [m.parent] : []), ...m.depends_on.map((d) => d.module)];
      if (needs.some((key) => out.has(key))) {
        out.add(m.key);
        spreading = true;
      }
    });
  }
  return out;
};

/** Permissions the draft's module levels include: shown as "from module". */
export const fromModules = (draft: RoleDraft, catalog: PermissionCatalog): Set<string> => {
  const modules = moduleMap(catalog);
  const keys = new Set<string>();
  Object.entries(draft.modules).forEach(([key, level]) =>
    (modules[key]?.includes[level] ?? []).forEach((k) => keys.add(k))
  );
  return keys;
};

/** Why a module is held without being chosen: its child or a dependant needs it. */
export const impliedBy = (
  draft: RoleDraft,
  catalog: PermissionCatalog
): Record<string, string[]> => {
  const reasons: Record<string, string[]> = {};
  catalog.modules.forEach((m) => {
    if (!draft.modules[m.key]) return;
    if (m.parent) (reasons[m.parent] ||= []).push(m.label);
    m.depends_on.forEach((d) => (reasons[d.module] ||= []).push(m.label));
  });
  return reasons;
};

/** Inherit on every dimension: where a newly granted scoped key starts (ORCH-415 Q12). */
const inheritCells = (dimensions: Dimension[]): Cells =>
  Object.fromEntries(dimensions.map((d) => [d.key, { mode: 'inherit', value_ids: [] }]));

const startingCells = (permission: PermissionDef | undefined, dimensions: Dimension[]): Cells =>
  permission?.scoped ? inheritCells(dimensions) : {};

export interface LevelChange {
  draft: RoleDraft;
  /** Scoped keys the level newly granted, started at Inherit on every dimension. */
  added: string[];
}

/**
 * Set a module's level and apply what follows from it
 *
 * Raising a level adds its parent (Read-only), its dependencies and the
 * permissions it includes; scoped ones start at Inherit on every dimension
 * (ORCH-415 Q12), and keys already granted keep their cells. Lowering it
 * removes the permissions only the old level included, and Hidden also hides
 * the modules beneath it, which would otherwise bring it back. A derived
 * module (the Admin Center) can't be set: it follows the modules under it.
 */
export const applyModuleLevel = (
  draft: RoleDraft,
  catalog: PermissionCatalog,
  dimensions: Dimension[],
  key: string,
  level: Level
): LevelChange => {
  const modules = moduleMap(catalog);
  const permissions = permissionMap(catalog);
  if (isDerived(modules[key])) return { draft, added: [] };
  const before = fromModules(draft, catalog);
  const next: Record<string, ModuleLevel> = { ...draft.modules };

  if (level) {
    next[key] = level;
  } else {
    const hide = [key];
    while (hide.length) {
      const gone = hide.pop() as string;
      delete next[gone];
      catalog.modules
        .filter(
          (m) => next[m.key] && (m.parent === gone || m.depends_on.some((d) => d.module === gone))
        )
        .forEach((m) => hide.push(m.key));
    }
  }

  // A held sub-module brings its parent; a held module brings its dependencies.
  const pending = Object.keys(next);
  while (pending.length) {
    const module = modules[pending.pop() as string];
    if (!module) continue;
    const needs: [string, ModuleLevel][] = [
      ...(module.parent ? [[module.parent, 'read'] as [string, ModuleLevel]] : []),
      ...module.depends_on.map((d) => [d.module, d.level] as [string, ModuleLevel]),
    ];
    needs.forEach(([needed, neededLevel]) => {
      if (rank(next[needed] ?? null) < rank(neededLevel)) {
        next[needed] = neededLevel;
        pending.push(needed);
      }
    });
  }
  // A derived module is never stored by the editor: the server derives it (ORCH-415)
  catalog.modules.filter(isDerived).forEach((m) => delete next[m.key]);

  const after = fromModules({ ...draft, modules: next }, catalog);
  const granted = { ...draft.permissions };
  const added: string[] = [];
  before.forEach((k) => {
    if (!after.has(k)) delete granted[k];
  });
  after.forEach((k) => {
    if (!granted[k] && permissions[k]) {
      granted[k] = startingCells(permissions[k], dimensions);
      if (permissions[k].scoped) added.push(k);
    }
  });
  return { draft: { ...draft, modules: next, permissions: granted }, added };
};

export const setModuleLevel = (
  draft: RoleDraft,
  catalog: PermissionCatalog,
  key: string,
  level: Level,
  dimensions: Dimension[] = []
): RoleDraft => applyModuleLevel(draft, catalog, dimensions, key, level).draft;

/** The keys that must be granted with `key`, nearest first (flight → plan view). */
export const requiredChain = (catalog: PermissionCatalog, key: string): string[] => {
  const permissions = permissionMap(catalog);
  const chain: string[] = [];
  let parent = permissions[key]?.requires ?? null;
  while (parent && !chain.includes(parent)) {
    chain.push(parent);
    parent = permissions[parent]?.requires ?? null;
  }
  return chain;
};

/** The granted keys that transitively require `key`. */
export const dependants = (draft: RoleDraft, catalog: PermissionCatalog, key: string): string[] =>
  Object.keys(draft.permissions).filter(
    (k) => k !== key && requiredChain(catalog, k).includes(key)
  );

export interface ToggleChange {
  draft: RoleDraft;
  /** Parents ticked along with the key (ORCH-415 §3.3). */
  autoTicked: string[];
  /** Dependants unticked along with the key. */
  cascaded: string[];
}

/**
 * Grant or remove a permission and apply the plan → flight → line item rule
 *
 * Ticking a key ticks the keys it requires. A parent already granted hands its
 * cells to the child; otherwise every new scoped key starts at Inherit on each
 * dimension (Q12). Unticking a key unticks everything that requires it.
 */
export const applyTogglePermission = (
  draft: RoleDraft,
  catalog: PermissionCatalog,
  dimensions: Dimension[],
  key: string,
  on: boolean
): ToggleChange => {
  const permissions = permissionMap(catalog);
  const granted = { ...draft.permissions };
  if (on) {
    const chain = requiredChain(catalog, key);
    const autoTicked = chain.filter((k) => !(k in granted));
    // The nearest already-granted ancestor's cells are the child's default
    const template = chain.find((k) => k in draft.permissions);
    const cellsFor = (k: string): Cells =>
      template && permissions[k]?.scoped
        ? { ...draft.permissions[template] }
        : startingCells(permissions[k], dimensions);
    [...autoTicked].reverse().forEach((k) => {
      granted[k] = cellsFor(k);
    });
    if (!(key in granted)) granted[key] = cellsFor(key);
    return { draft: { ...draft, permissions: granted }, autoTicked, cascaded: [] };
  }
  const cascaded = dependants(draft, catalog, key);
  [key, ...cascaded].forEach((k) => delete granted[k]);
  return { draft: { ...draft, permissions: granted }, autoTicked: [], cascaded };
};

export const togglePermission = (
  draft: RoleDraft,
  key: string,
  on: boolean,
  catalog?: PermissionCatalog,
  dimensions: Dimension[] = []
): RoleDraft => {
  if (!catalog) {
    const permissions = { ...draft.permissions };
    if (on) permissions[key] = permissions[key] ?? {};
    else delete permissions[key];
    return { ...draft, permissions };
  }
  return applyTogglePermission(draft, catalog, dimensions, key, on).draft;
};

export const setCell = (
  draft: RoleDraft,
  key: string,
  dimension: string,
  scope: Scope | null
): RoleDraft => {
  const cells = { ...(draft.permissions[key] ?? {}) };
  if (scope) cells[dimension] = scope;
  else delete cells[dimension];
  return { ...draft, permissions: { ...draft.permissions, [key]: cells } };
};

/** Row shortcut: one mode on every dimension of a permission. */
export const setRow = (
  draft: RoleDraft,
  key: string,
  dimensions: Dimension[],
  mode: 'all' | 'inherit'
): RoleDraft =>
  dimensions.reduce((d, dim) => setCell(d, key, dim.key, { mode, value_ids: [] }), draft);

/**
 * Column shortcut: ALL on one dimension for every granted scoped permission
 * the editor holds; rows they don't hold stay as they are (decision #62).
 */
export const setColumnAll = (
  draft: RoleDraft,
  catalog: PermissionCatalog,
  dimension: string,
  holds: (key: string) => boolean = () => true
): RoleDraft => {
  const permissions = permissionMap(catalog);
  return Object.keys(draft.permissions)
    .filter((k) => permissions[k]?.scoped && holds(k))
    .reduce((d, k) => setCell(d, k, dimension, { mode: 'all', value_ids: [] }), draft);
};

/** Is a cell complete? Override needs at least one value. */
export const cellComplete = (scope: Scope | undefined) =>
  Boolean(scope) && (scope?.mode !== 'override' || (scope?.value_ids.length ?? 0) > 0);

/** Granted scoped permissions missing a complete cell on some dimension: save is blocked. */
export const scopeNeeded = (
  draft: RoleDraft,
  catalog: PermissionCatalog,
  dimensions: Dimension[]
): string[] => {
  const permissions = permissionMap(catalog);
  return Object.entries(draft.permissions)
    .filter(([k]) => permissions[k]?.scoped)
    .filter(([, cells]) => dimensions.some((d) => !cellComplete(cells[d.key])))
    .map(([k]) => k);
};

/** Likely mistakes that don't block a save (§3.12 rule 8); the plan → flight rule is in `requires`. */
export const warnings = (draft: RoleDraft, labels: Record<string, string>): string[] => {
  const out: string[] = [];
  const has = (k: string) => k in draft.permissions;
  if (has(PermissionKey.MDM_INLINE_CREATE) && !(ModuleKey.PLANNING in draft.modules)) {
    out.push(
      `${labels[PermissionKey.MDM_INLINE_CREATE] ?? 'Add new in dropdowns'} has no use without Planning access`
    );
  }
  return out;
};
